/**
 * NotesFieldControl — append-only notes thread for a Notes-type field.
 * Notes are persisted on the record under `data.{field_name}` as a list of
 * {id, text, user_id, user_name, at}.
 *
 * Props:
 *  - field: FormField metadata
 *  - value: list of existing notes (from data[field_name])
 *  - onChange(v): bubble new list to renderer (so save still works)
 *  - formId, recordId: required for the POST endpoint
 *  - disabled: read-only mode (still shows existing notes; hides composer)
 *  - testid
 *
 * UX:
 *  - Existing notes rendered chronologically (newest first), user + date stamp + plain text.
 *  - Composer at top: textarea + Post button. Posts to /records/{form_id}/{record_id}/notes/{field_name}.
 *  - If recordId is undefined (record not yet saved), the composer is disabled with a hint.
 */
import { useEffect, useState, useRef } from "react";
import { api, extractError } from "@/lib/api";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ChatCircleText, PaperPlaneTilt } from "@phosphor-icons/react";
import { toast } from "sonner";

function fmtWhen(iso) {
    try {
        const d = new Date(iso);
        const now = new Date();
        const diffMin = Math.round((now - d) / 60000);
        if (diffMin < 1) return "just now";
        if (diffMin < 60) return `${diffMin}m ago`;
        if (diffMin < 60 * 24) return `${Math.round(diffMin / 60)}h ago`;
        return d.toLocaleDateString() + " " + d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    } catch {
        return iso || "";
    }
}

export default function NotesFieldControl({ field, value, onChange, formId, recordId, disabled, testid }) {
    const [notes, setNotes] = useState(Array.isArray(value) ? value : []);
    const [draft, setDraft] = useState("");
    const [posting, setPosting] = useState(false);
    const lastSyncedRef = useRef("");

    // Sync internal state when parent value changes (e.g. record reload)
    useEffect(() => {
        const sig = JSON.stringify(value || []);
        if (sig !== lastSyncedRef.current) {
            lastSyncedRef.current = sig;
            setNotes(Array.isArray(value) ? value : []);
        }
    }, [value]);

    const canPost = !!recordId && !disabled;

    const post = async () => {
        const text = draft.trim();
        if (!text) return;
        if (!recordId) { toast.error("Save the record first to start a notes thread."); return; }
        setPosting(true);
        try {
            const { data } = await api.post(`/records/${formId}/${recordId}/notes/${field.field_name}`, { text });
            const next = data.items || [];
            setNotes(next);
            lastSyncedRef.current = JSON.stringify(next);
            onChange?.(next);
            setDraft("");
        } catch (e) {
            toast.error(extractError(e));
        } finally {
            setPosting(false);
        }
    };

    // Newest first
    const sorted = [...notes].sort((a, b) => (b.at || "").localeCompare(a.at || ""));

    return (
        <div className="border border-border rounded-sm bg-card" data-testid={testid}>
            {canPost && (
                <div className="border-b border-border p-3 space-y-2">
                    <Textarea
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        placeholder="Add a note…"
                        rows={2}
                        className="rounded-sm resize-none"
                        data-testid={`${testid}-input`}
                        maxLength={5000}
                    />
                    <div className="flex items-center justify-between">
                        <div className="text-[10px] text-muted-foreground font-mono">{draft.length}/5000 · append-only</div>
                        <Button
                            type="button" size="sm" className="rounded-sm bg-primary hover:bg-primary/90 text-primary-foreground"
                            onClick={post} disabled={posting || !draft.trim()}
                            data-testid={`${testid}-post-btn`}
                        >
                            <PaperPlaneTilt size={12} className="mr-1" />
                            {posting ? "Posting…" : "Post note"}
                        </Button>
                    </div>
                </div>
            )}
            {!recordId && !disabled && (
                <div className="border-b border-border p-3 text-[11px] text-muted-foreground italic flex items-center gap-2" data-testid={`${testid}-pre-save-hint`}>
                    <ChatCircleText size={12} /> Save the record first to start the notes thread.
                </div>
            )}
            <div className="max-h-96 overflow-y-auto divide-y divide-border" data-testid={`${testid}-list`}>
                {sorted.length === 0 ? (
                    <div className="p-4 text-xs text-muted-foreground text-center">No notes yet.</div>
                ) : sorted.map((n) => (
                    <div key={n.id || `${n.user_id}-${n.at}`} className="p-3" data-testid={`${testid}-item-${n.id || ""}`}>
                        <div className="flex items-center gap-2 mb-1">
                            <Badge variant="outline" className="rounded-sm font-mono text-[9px]">{n.user_name || "user"}</Badge>
                            <span className="text-[10px] text-muted-foreground font-mono">{fmtWhen(n.at)}</span>
                        </div>
                        <div className="text-sm whitespace-pre-wrap break-words">{n.text}</div>
                    </div>
                ))}
            </div>
        </div>
    );
}
